<template>
  <div>
    <div class="row fit no-wrap justify-center items-end content-start q-pb-md">
      <div class="col-grow q-pr-lg">
        <span class="midia-app-caption-sm">Nome do Cadastro *</span>
        <q-input
          v-model="formData.nome"
          outlined
          dense
          placeholder="Informe o nome do cadastro"
        />
      </div>

      <div style="width: 160px;">
        <span class="midia-app-caption-sm">Período de Vigência *</span>
        <q-input
        style="outline: 1px solid var(--globalAppSecondaryColor2);"
        v-model="formData.dtIniVigencia"
        mask="##/##/####"
        borderless
        dense
        @update:model-value="updateStartDate"
      >
        <template v-slot:prepend>
          <q-icon class="cursor-pointer q-pa-sm q-mr-sm" style="background: var(--globalAppPrimaryColor);">
            <img class="svg-icon" src="@/assets/icons/icon_HiOutlineCalendar.svg" />

            <q-popup-proxy ref="qPopupProxyStartDate" cover transition-show="scale" transition-hide="scale">
              <q-date
                v-model="startDateInternal"
                mask="YYYY-MM-DD"
                minimal
                color="globalAppPrimaryColor"
                @update:model-value="updateStartDateFormatted"
              >
                <div class="row items-center justify-end">
                  <ButtonCloseModal />
                </div>
              </q-date>
            </q-popup-proxy>

          </q-icon>
        </template>
      </q-input>

      </div>

      <div class="q-px-sm q-pb-sm">
        <span class="midia-app-caption-sm">até</span>
      </div>

      <div style="width: 160px;">
        <q-input
        style="outline: 1px solid var(--globalAppSecondaryColor2);"
        v-model="formData.dtFimVigencia"
        mask="##/##/####"
        borderless
        dense
        @update:model-value="updateEndDate"
      >
        <template v-slot:prepend>
          <q-icon class="cursor-pointer q-pa-sm q-mr-sm" style="background: var(--globalAppPrimaryColor);">
            <img class="svg-icon" src="@/assets/icons/icon_HiOutlineCalendar.svg" />

            <q-popup-proxy ref="qPopupProxyEndDate" cover transition-show="scale" transition-hide="scale">
              <q-date
                v-model="endDateInternal"
                mask="YYYY-MM-DD"
                minimal
                color="globalAppPrimaryColor"
                @update:model-value="updateEndDateFormatted"
              >
                <div class="row items-center justify-end">
                  <ButtonCloseModal />
                </div>
              </q-date>
            </q-popup-proxy>

          </q-icon>
        </template>
      </q-input>
      </div>

    </div>

    <div class="row q-pb-md">
      <div class="col-6">
        <span class="midia-app-caption-sm">Perfil *</span>
        <q-select
          v-model="formData.perfil"
          outlined
          dense
          :options="['Banco24Horas', 'ATM Manager']"
          :disable="authStore.currentUserInstitutionalProfile !== 'ADMIN'"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInstitutionalStore } from '@/stores/institutionalStore';
import { ref, computed, onMounted } from 'vue'
import { QPopupProxy } from 'quasar'
import ButtonCloseModal from '@/components/ButtonCloseModal.vue'
import { useAuthStore } from '@/stores/authStore'
import type { InstitutionalDraft } from '@/stores/institutionalStore';
import type { TelasInstitucionaisModel } from '@/models/TelasInstitucionaisModel';


const qPopupProxyStartDate: { value: QPopupProxy | null } = ref(null)
const qPopupProxyEndDate: { value: QPopupProxy | null } = ref(null)

const startDateInternal = ref('')
const endDateInternal = ref('')

const authStore = useAuthStore()

const perfilOptions = computed(() => {
  return ['Banco24Horas', 'ATM Manager']
})

const perfilDisabled = computed(() => {
  return !authStore.currentUserRoles.userIsAdmin
})





const updateStartDate = () => {
  const target = institutionalStore.institutionalDetail ?? institutionalStore.institutionalDraft;

  if (target && target.dtIniVigencia) {
    startDateInternal.value =
      target.dtIniVigencia.split('/').reverse().join('-');
  }
};

const updateStartDateFormatted = () => {
  const target = institutionalStore.institutionalDetail ?? institutionalStore.institutionalDraft;

  if (target && startDateInternal.value) {
    target.dtIniVigencia =
      startDateInternal.value.split('-').reverse().join('/');
  }

  qPopupProxyStartDate.value?.hide();
};


const updateEndDate = () => {
  const target =
    institutionalStore.institutionalDetail ??
    institutionalStore.institutionalDraft;

  if (target && target.dtFimVigencia) {
    endDateInternal.value =
      target.dtFimVigencia.split('/').reverse().join('-');
  }
};


const updateEndDateFormatted = () => {
  const target =
    institutionalStore.institutionalDetail ??
    institutionalStore.institutionalDraft;

  if (target && endDateInternal.value) {
    target.dtFimVigencia =
      endDateInternal.value.split('-').reverse().join('/');
  }

  qPopupProxyEndDate.value?.hide();
};




const formData = computed<TelasInstitucionaisModel | InstitutionalDraft>(() => {
  return institutionalStore.institutionalDetail || institutionalStore.institutionalDraft;
});


onMounted(() => {
  const perfil = authStore.currentUserInstitutionalProfile;

  const target =
    institutionalStore.institutionalDetail ??
    institutionalStore.institutionalDraft;

  if (!target) return;

  if (perfil === 'B24H') {
    target.perfil = 'Banco24Horas';
  }

  if (perfil === 'ATMM') {
    target.perfil = 'ATM Manager';
  }

  syncDatesFromBackend();
});

const formatToBR = (date?: string | null) => {
  if (!date) return '';

  // já está certo
  if (date.includes('/')) return date;

  // formato backend YYYY-MM-DD
  if (date.includes('-')) {
    return date.split('-').reverse().join('/');
  }

  return date;
};

const syncDatesFromBackend = () => {
  const target =
    institutionalStore.institutionalDetail ??
    institutionalStore.institutionalDraft;

  if (!target) return;

  target.dtIniVigencia = formatToBR(target.dtIniVigencia);
  target.dtFimVigencia = formatToBR(target.dtFimVigencia);

  // sincroniza também o internal (calendar)
  if (target.dtIniVigencia) {
    startDateInternal.value =
      target.dtIniVigencia.split('/').reverse().join('-');
  }

  if (target.dtFimVigencia) {
    endDateInternal.value =
      target.dtFimVigencia.split('/').reverse().join('-');
  }
};

const institutionalStore = useInstitutionalStore();
</script>

<style scoped>
</style>